import io from 'socket.io-client';

class WebSocketManager {

    authConfig = null;
    baseUrl = null;
    connected = false;
    listeners = [];
    messageQueues = [];
    maxReconnectAttempts = 5;
    namespaces = [];
    reconnectAttempts = [];
    reconnectDelay = 1000;
    socket = null;
    
    constructor() {
        this.listeners = new Map();
        this.messageQueues = new Map();
        this.namespaces = new Map();
        this.reconnectAttempts = new Map();
    }

    connect({ entity, user, url}) {

        // disconnect from existing sockets if applicable
        this.disconnect();

        // parse URL to separate base URL and path
        let parsedUrl = new URL(url);
        this.baseUrl = `${parsedUrl.protocol}//${parsedUrl.host}`;
        let socketPath = parsedUrl.pathname === '/' ? '/socket.io/' : `${parsedUrl.pathname}/socket.io/`;

        // store auth configuration for namespace connections
        this.authConfig = {
            auth: { 
                entity_token: entity?.token || null,
                token: user?.token || null
            },
            forceNew: true,
            path: socketPath,
            rememberUpgrade: true,
            timeout: 10000,
            transports: ['websocket'],
            upgrade: true
        };

        // create main base socket connection
        this.socket = io(this.baseUrl, this.authConfig);
        this.setupMainEventHandlers();

        // connect to default namespaces
        ['accounts','nova','payments','requiem','secure_portal','trigger_instructions','users'].forEach(this.connectToNamespace.bind(this));
    }

    connectToNamespace(namespace) {

        // reconnect to existing namespace if socket already exists
        if(this.namespaces.has(namespace) === true) {
            return;
        }

        // skip connection if namespace requires auth and no user auth is available
        if(['accounts','trigger_instructions','nova','payments','users'].includes(namespace) && this.authConfig?.auth?.token === null) {
            return;
        }

        // skip connection if namespace requires entity auth and no entity auth is available
        if(namespace === 'secure_portal' && this.authConfig?.auth?.entity_token === null) {
            return;
        }

        // create socket connection for specific namespace
        let namespaceSocket = io(`${this.baseUrl}/${namespace}`, this.authConfig);

        // store the namespace socket
        this.namespaces.set(namespace, namespaceSocket);
        
        // initialize data structures for this namespace
        if(!this.listeners.has(namespace) === true) {
            this.listeners.set(namespace, new Map());
        }
        if(!this.messageQueues.has(namespace) === true) {
            this.messageQueues.set(namespace, []);
        }
        if(!this.reconnectAttempts.has(namespace) === true) {
            this.reconnectAttempts.set(namespace, 0);
        }

        // setup event handlers for this namespace socket
        this.setupNamespaceEventHandlers(namespace, namespaceSocket);
    }

    disconnect() {

        // disconnect from main socket if applicable
        if(this.socket) {
            this.socket?.disconnect();
        }

        // disconnect from all namespace sockets
        this.namespaces.forEach(socket => {
            socket?.disconnect();
        });
        this.namespaces.clear();

        // clear all data structures
        this.connected = false;
        this.messageQueues.clear();
        this.reconnectAttempts.clear();
        this.listeners.clear();
    }

    emit(namespace, event, data) {
        
        // emit to specific namespace listeners
        if (this.listeners.has(namespace) && this.listeners.get(namespace).has(event)) {
            this.listeners.get(namespace).get(event).forEach(callback => {
                callback(data);
            });
        }
    }

    flushMessageQueue(namespace) {

        let messageQueue = this.messageQueues.get(namespace);
        let socket = this.namespaces.get(namespace);
        if(!messageQueue || !socket) {
            return;
        }

        while(messageQueue.length > 0 && socket.connected) {
            let { event, data } = messageQueue.shift();
            socket.emit(event, data);
        }
    }

    handleReconnect(namespace) {

        let attempts = this.reconnectAttempts.get(namespace) || 0;

        // no additional logic is required if max reconnection attempts have been met
        if(attempts >= this.maxReconnectAttempts) {
            this.emit(namespace, 'max_reconnect_attempts', { namespace });
            this.disconnect();
            return;
        }

        // increment attempts and retry connection with delay
        this.reconnectAttempts.set(namespace, attempts + 1);
        let delay = this.reconnectDelay * Math.pow(2, attempts);
        console.warn(`[websockets]: attempting to reconnect ${namespace} in ${delay}ms... (${attempts + 1}/${this.maxReconnectAttempts})`);
        
        setTimeout(() => {
            let socket = this.namespaces.get(namespace);
            if(socket) {
                socket.connect();
            }
        }, delay);
    }

    off(namespace, event, callback) {
        
        // ensure namespace socket exists, then retry operation when connected
        if(!this.namespaces.has(namespace)) {
            this.connectToNamespace(namespace);

            // wait for namespace to be created, then retry operation
            const checkConnection = () => {
                if(this.namespaces.has(namespace)) {
                    this.off(namespace, event, callback);
                } else {
                    // retry after a short delay if namespace still doesn't exist
                    setTimeout(checkConnection, 50);
                }
            };
            checkConnection();
            return;
        }
        
        // determine if namespace and event exist within listeners map
        if(this.listeners.has(namespace) && this.listeners.get(namespace).has(event)) {
            this.listeners.get(namespace).get(event).delete(callback);
            
            // if no more listeners for this event, remove socket listener
            if(this.listeners.get(namespace).get(event).size === 0) {
                this.listeners.get(namespace).delete(event);
                let socket = this.namespaces.get(namespace);
                if(socket) {
                    socket.off(event);
                }
            }
        }
    }

    on(namespace, event, callback) {
        
        // ensure namespace socket exists, then retry operation when connected
        if(!this.namespaces.has(namespace)) {
            this.connectToNamespace(namespace);

            // wait for namespace to be created, then retry operation
            const checkConnection = () => {
                if(this.namespaces.has(namespace)) {
                    this.on(namespace, event, callback);
                } else {
                    // retry after a short delay if namespace still doesn't exist
                    setTimeout(checkConnection, 50);
                }
            };
            checkConnection();
            return;
        }

        // ensure namespace listeners map exists
        if(!this.listeners.has(namespace)) {
            this.listeners.set(namespace, new Map());
        }

        let namespaceListeners = this.listeners.get(namespace);

        // add to internal listeners map
        if(namespaceListeners.has(event) === false) {
            namespaceListeners.set(event, new Set());
            
            // register socket listener for this event if socket exists
            let socket = this.namespaces.get(namespace);
            if(socket) {
                socket.on(event, (data) => {
                    this.emit(namespace, event, data);
                });
            }
        }
        namespaceListeners.get(event).add(callback);
    }

    send(namespace, event, data = {}) {

        // ensure namespace socket exists, then retry operation when connected
        if (!this.namespaces.has(namespace)) {
            this.connectToNamespace(namespace);

            // wait for namespace to be created, then retry operation
            const checkConnection = () => {
                if(this.namespaces.has(namespace)) {
                    this.send(namespace, event, data);
                } else {
                    // retry after a short delay if namespace still doesn't exist
                    setTimeout(checkConnection, 50);
                }
            };
            checkConnection();
            return;
        }

        let socket = this.namespaces.get(namespace);
        
        // send message to server if socket is connected
        if(socket && socket.connected) {
            socket.emit(event, data);
            return;
        }

        // fallback to queue message for when connection is restored
        console.log(`[websockets]: queuing ${namespace}/${event} message`, data);
        if (!this.messageQueues.has(namespace)) {
            this.messageQueues.set(namespace, []);
        }
        this.messageQueues.get(namespace).push({ event, data });
    }

    setupMainEventHandlers() {
        
        if(!this.socket) {
            return;
        }

        // register connection handler for main socket
        this.socket.on('connect', () => {

            console.log('[websockets]: connected');
            this.connected = true;
            
            // notify listeners that main connection has completed
            this.emit('main', 'connected', {});
        });

        // register connection error handler for main socket
        this.socket.on('connect_error', error => {
            console.error('[websockets]: connection error:', error);
            this.connected = false;
            this.emit('main', 'error', error);
        });

        // register disconnection handler for main socket
        this.socket.on('disconnect', (reason) => {
            console.log(`[websockets]: disconnected:`, reason);
            this.connected = false;
            this.emit('main', 'disconnected', reason);
        });

        // register default message and error handlers for main socket
        this.socket.on('message', data => this.emit('main', 'message', data));
        this.socket.on('error', error => this.emit('main', 'error', error));
    }

    setupNamespaceEventHandlers(namespace, socket) {
        
        if(!socket) {
            return;
        }

        // register connection handler
        socket.on('connect', () => {
            console.log(`[websockets]: ${namespace} connected`);
            
            // reset reconnection attempts on successful connection
            this.reconnectAttempts.set(namespace, 0);
            
            // send any queued messages that may have accumulated 
            this.flushMessageQueue(namespace);
            
            // notify listeners that connection has completed
            this.listeners.get(namespace).get('connect')?.forEach(callback => {
                callback();
            });
        });

        // register connection error handler
        socket.on('connect_error', error => {
            console.error(`[websockets]: ${namespace} connection error:`, error);
            if(error.message?.includes('authentication failed') === true) {
                return;
            }
            this.emit(namespace, 'error', { namespace, error });
            this.handleReconnect(namespace);
        });

        // register disconnection handler
        socket.on('disconnect', (reason) => {
            console.log(`[websockets]: ${namespace} disconnected:`, reason);
            
            // notify subscribers
            this.emit(namespace, 'disconnected', { namespace, reason });
            
            // attempt reconnection unless the disconnection was intentional
            if(reason !== 'io client disconnect') {
                this.handleReconnect(namespace);
            }

            // notify listeners that connection has completed
            this.listeners.get(namespace).get('disconnect')?.forEach(callback => {
                callback(reason);
            });
        });

        // register socket listeners for all custom events that were added before connection
        if (this.listeners.has(namespace)) {
            this.listeners.get(namespace).forEach((callbacks, event) => {
                if(!['connect', 'connect_error', 'disconnect', 'error'].includes(event)) {
                    socket.on(event, (data) => {
                        this.emit(namespace, event, data);
                    });
                }
            });
        }

        // register default message and error handlers
        socket.on('message', data => this.emit(namespace, 'message', data));
        socket.on('error', error => this.emit(namespace, 'error', { namespace, error }));
    }

    removeEventHandlers() {
        // clear all listeners
        this.listeners?.clear();
        
        // remove listeners from main socket
        if(this.socket) {
            this.socket.removeAllListeners();
        }
        
        // remove listeners from all namespace sockets
        this.namespaces.forEach((socket) => {
            socket.removeAllListeners();
        });
    }
}

export default WebSocketManager;